import React, { useEffect, useState } from "react";
import { Card, Table, Pagination, Form, InputGroup, Button } from "react-bootstrap";
import { IconSearch, IconChevronUp, IconChevronDown, IconDownload } from "@tabler/icons-react";
import { fetchWithAuth } from "../utils/fetchWithAuth";
import { Helmet } from "react-helmet-async";

const AllOrders = () => {
  const [orders, setOrders] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [downloadingRef, setDownloadingRef] = useState(null);

  useEffect(() => {
    fetchOrders();
  }, [page, limit, searchQuery, sortBy, sortOrder]);

  const fetchOrders = async () => {
    try {
      const response = await fetchWithAuth("/api/admin/all-orders");
      const data = await response.json();

      let filtered = data.filter(order =>
        order.order_reference.toLowerCase().includes(searchQuery.toLowerCase()) ||
        order.product.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (order.user_email || "").toLowerCase().includes(searchQuery.toLowerCase())
      );

      filtered = filtered.sort((a, b) => {
        const aVal = a[sortBy];
        const bVal = b[sortBy];
        if (aVal < bVal) return sortOrder === "asc" ? -1 : 1;
        if (aVal > bVal) return sortOrder === "asc" ? 1 : -1;
        return 0;
      });

      setOrders(filtered);
    } catch (error) {
      console.error("Ошибка при загрузке заказов:", error);
    }
  };

  const total = orders.length;
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const paginated = orders.slice((page - 1) * limit, page * limit);

  const getPaginationPages = (current, total) => {
    const delta = 2;
    const pages = new Set([1, total]);
    for (let i = current - delta; i <= current + delta; i++) {
      if (i > 1 && i < total) pages.add(i);
    }
    const sorted = Array.from(pages).sort((a, b) => a - b);
    const result = [];
    for (let i = 0; i < sorted.length; i++) {
      result.push(sorted[i]);
      if (i < sorted.length - 1 && sorted[i + 1] - sorted[i] > 1) {
        result.push("...");
      }
    }
    return result;
  };

  const renderSortHeader = (label, key, className = "") => (
    <th
      onClick={() => {
        if (sortBy === key) {
          setSortOrder(sortOrder === "asc" ? "desc" : "asc");
        } else {
          setSortBy(key);
          setSortOrder("asc");
        }
      }}
      style={{ cursor: "pointer", whiteSpace: "nowrap" }}
      className={className}
    >
      {label}{" "}
      {sortBy === key &&
        (sortOrder === "asc" ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />)}
    </th>
  );

  const handleDelete = async () => {
    const confirmed = window.confirm(
      `Are you sure you want to delete ${selectedOrders.length} order${selectedOrders.length > 1 ? "s" : ""}?`
    );
    if (!confirmed) return;

    try {
      await fetchWithAuth(
        `/api/admin/delete-orders?${selectedOrders.map((id) => `ids=${id}`).join("&")}`,
        { method: "DELETE" }
      );
      setSelectedOrders([]);
      fetchOrders();
    } catch (error) {
      console.error("Error deleting orders:", error);
    }
  };

  // --- filename from Content-Disposition ---
  const pickFilename = (res, fallback) => {
    const cd = res.headers?.get?.("content-disposition") || "";
    const m = /filename\*=UTF-8''([^;]+)|filename="?([^"]+)"?/i.exec(cd);
    if (m) return decodeURIComponent(m[1] || m[2] || fallback);
    return fallback;
  };

  // === NEW: admin download as BLOB (Authorization header included) ===
  const handleDownload = async (reference, price) => {
    const total = Number(price || 0);
    if (!reference || !isFinite(total) || total <= 0) return;

    setDownloadingRef(reference);
    try {
      const res = await fetchWithAuth(`/api/admin/orders/${encodeURIComponent(reference)}/invoice`, {
        method: "GET",
      });
      if (!res.ok) {
        console.error("Admin invoice download error:", res.status);
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const filename = pickFilename(res, `invoice_${reference}.pdf`);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error("Admin invoice download error:", e);
    } finally {
      setDownloadingRef(null);
    }
  };

  const pagesToShow = getPaginationPages(page, totalPages);
  return (
    <>
      <Helmet> <title>All Orders - IndexBooster</title></Helmet>
      <Card className="mt-4">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h3 className="m-0">All Orders (Admin only)</h3>
          <InputGroup style={{ maxWidth: 300 }}>
            <InputGroup.Text>
              <IconSearch size={18} />
            </InputGroup.Text>
            <Form.Control
              placeholder="Search order or email..."
              value={searchQuery}
              onChange={(e) => {
                setPage(1);
                setSearchQuery(e.target.value);
              }}
            />
          </InputGroup>
        </Card.Header>

        <div className="table-responsive">
          <Table className="table card-table table-vcenter text-nowrap datatable" hover responsive>
            <thead>
              <tr>
                <th></th>
                {renderSortHeader("User", "user_email")}
                {renderSortHeader("Reference", "order_reference")}
                {renderSortHeader("Date", "created_at")}
                {renderSortHeader("Status", "status", "text-center")}
                {renderSortHeader("Product", "product", "text-center")}
                {renderSortHeader("Price", "price", "text-center")}
                {renderSortHeader("Credits", "credits", "text-center")}
                <th className="text-center">Invoice</th>
              </tr>
            </thead>
            <tbody>
              {paginated.length === 0 ? (
                <tr>
                  <td colSpan="9" className="text-center py-4">
                    No Orders Found
                  </td>
                </tr>
              ) : (
                paginated.map((order, index) => {
                  const total = Number(order.price || 0);
                  const showDash = !isFinite(total) || total <= 0;

                  return (
                    <tr key={index}>
                      <td className="text-center">
                        <Form.Check
                          type="checkbox"
                          checked={selectedOrders.includes(order.id)}
                          onChange={() => {
                            setSelectedOrders((prev) =>
                              prev.includes(order.id)
                                ? prev.filter((id) => id !== order.id)
                                : [...prev, order.id]
                            );
                          }}
                        />
                      </td>
                      <td>{order.user_email}</td>
                      <td>{order.order_reference}</td>
                      <td>
                        {new Date(order.created_at).toLocaleDateString()}<br />
                        {new Date(order.created_at).toLocaleTimeString()}
                      </td>
                      <td className="text-center">{order.status}</td>
                      <td className="text-center">{order.product}</td>
                      <td className="text-center">{order.price} {order.currency}</td>
                      <td className="text-center">{order.credits}</td>
                      <td className="text-center">
                        {showDash ? (
                          <span className="text-muted">—</span>
                        ) : (
                          <Button
                            size="md"
                            onClick={() => handleDownload(order.order_reference, order.price)}
                            disabled={downloadingRef === order.order_reference}
                          >
                            <IconDownload size={16} className="me-1" />
                            {downloadingRef === order.order_reference ? "Preparing…" : "Download"}
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </Table>
        </div>

        <Card.Footer className="d-flex flex-wrap justify-content-between align-items-center px-4">
          <div className="d-flex align-items-center gap-3 mb-2 mb-md-0">
            <Button
              variant="danger"
              disabled={selectedOrders.length === 0}
              onClick={handleDelete}
            >
              Remove
            </Button>
            <Pagination className="mb-0">
              <Pagination.Prev disabled={page === 1} onClick={() => setPage(page - 1)}>«</Pagination.Prev>
              {pagesToShow.map((p, i) =>
                p === "..." ? (
                  <Pagination.Ellipsis key={`ellipsis-${i}`} disabled />
                ) : (
                  <Pagination.Item key={`page-${p}`} active={p === page} onClick={() => setPage(p)}>
                    {p}
                  </Pagination.Item>
                )
              )}
              <Pagination.Next disabled={page === totalPages} onClick={() => setPage(page + 1)}>»</Pagination.Next>
            </Pagination>
            <div className="text-muted">
              Showing <b>{(page - 1) * limit + 1}</b>–<b>{Math.min(page * limit, total)}</b> of <b>{total}</b> entries
            </div>
          </div>
          <div className="d-flex align-items-center gap-2">
            <span className="text-muted">Show</span>
            <Form.Select
              value={limit}
              onChange={(e) => {
                setPage(1);
                setLimit(parseInt(e.target.value));
              }}
              size="sm"
            >
              {[25, 50, 100, 200].map((val) => (
                <option key={val} value={val}>{val}</option>
              ))}
            </Form.Select>
            <span className="text-muted">entries</span>
          </div>
        </Card.Footer>
      </Card>
    </>
  );
};

export default AllOrders;
